// Long-press detector for touch/pen/mouse pointer events (pure, timer-injectable for tests).
export const LONG_PRESS_MS = 500;
export const LONG_PRESS_SLOP = 10;

type Pt = { clientX: number; clientY: number };
type Timers = { set: (fn: () => void, ms: number) => unknown; clear: (id: unknown) => void };

const realTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
};

/**
 * Fires `onLongPress` after `delay` ms of a still press; a move beyond `slop` px,
 * a scroll, release or cancel stops it. When it fired, the next click is swallowed
 * so the tap-to-open never runs after a long-press.
 */
export function createLongPress(onLongPress: () => void, { delay = LONG_PRESS_MS, slop = LONG_PRESS_SLOP, timers = realTimers } = {}) {
  let timer: unknown = null;
  let start: Pt | null = null;
  let fired = false;
  const cancel = () => {
    if (timer !== null) timers.clear(timer);
    timer = null;
    start = null;
  };
  return {
    down(p: Pt) {
      cancel();
      fired = false;
      start = { clientX: p.clientX, clientY: p.clientY };
      timer = timers.set(() => {
        timer = null;
        fired = true;
        onLongPress();
      }, delay);
    },
    move(p: Pt) {
      if (!start) return;
      if (Math.hypot(p.clientX - start.clientX, p.clientY - start.clientY) > slop) cancel();
    },
    cancel,
    /** Returns true when the click should be suppressed (a long-press just fired). */
    consumeClick() {
      const f = fired;
      fired = false;
      return f;
    },
  };
}
